import React, { useState } from 'react';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { TextInput } from '../../../shared/components/TextInput';
import { authApi } from '../../../shared/lib/api';

interface ResetPasswordPageProps {
  /** Token cru vindo do link do e-mail (`/reset-password?token=...`). */
  token: string | null;
  /** Volta para a tela de login limpando o token da URL. */
  onDone: () => void;
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ token, onDone }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({ next: false, confirm: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const appDescription = import.meta.env.VITE_APP_DESCRIPTION || 'Sistema de Gestão';
  const companyName = import.meta.env.VITE_COMPANY_NAME || 'Sua Empresa';
  const currentYear = new Date().getFullYear();

  const validate = () => {
    const next: Record<string, string> = {};
    // Espelha as regras do ResetPasswordDto no backend.
    if (!newPassword) {
      next.newPassword = 'Nova senha é obrigatória';
    } else if (newPassword.length < 8) {
      next.newPassword = 'Senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[A-Z])/.test(newPassword)) {
      next.newPassword = 'Senha deve conter pelo menos uma letra maiúscula';
    } else if (!/(?=.*\d)/.test(newPassword)) {
      next.newPassword = 'Senha deve conter pelo menos um número';
    } else if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(newPassword)) {
      next.newPassword = 'Senha deve conter pelo menos um caractere especial';
    }
    if (!confirmPassword) {
      next.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (newPassword !== confirmPassword) {
      next.confirmPassword = 'Senhas não conferem';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !validate()) return;

    setIsLoading(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setDone(true);
    } catch (error: any) {
      setErrors({ submit: error?.message || 'Erro ao redefinir senha. Tente novamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const eyeButton = (field: 'next' | 'confirm') => (
    <button
      type="button"
      onClick={() => setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))}
      className="text-gray-400 hover:text-gray-600"
    >
      {showPasswords[field] ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md md:max-w-lg">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          <div className="flex justify-center mb-6">
            <Logo className="h-16" />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{appDescription}</h2>
            <p className="mt-2 text-sm text-gray-600">Defina sua nova senha de acesso</p>
          </div>

          {!token ? (
            <div className="space-y-6">
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <p className="text-sm text-red-800 flex items-start">
                  <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  Link inválido — nenhum token de redefinição encontrado. Solicite um novo link em
                  "Esqueci minha senha".
                </p>
              </div>
              <button
                type="button"
                onClick={onDone}
                className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Voltar para o login
              </button>
            </div>
          ) : done ? (
            <div className="space-y-6">
              <div className="rounded-md bg-green-50 p-4 border border-green-200">
                <p className="text-sm text-green-800 flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  Senha redefinida com sucesso. Use a nova senha para entrar.
                </p>
              </div>
              <button
                type="button"
                onClick={onDone}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Ir para o login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <TextInput
                label="Nova senha"
                type={showPasswords.next ? 'text' : 'password'}
                icon={<Lock className="w-5 h-5" />}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword) setErrors(prev => ({ ...prev, newPassword: '' }));
                }}
                error={errors.newPassword}
                autoComplete="new-password"
                rightElement={eyeButton('next')}
              />

              <TextInput
                label="Confirmar nova senha"
                type={showPasswords.confirm ? 'text' : 'password'}
                icon={<Lock className="w-5 h-5" />}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                }}
                error={errors.confirmPassword}
                autoComplete="new-password"
                rightElement={eyeButton('confirm')}
              />

              <p className="text-xs text-gray-500">
                A senha deve conter pelo menos 8 caracteres, incluindo letra maiúscula, número e
                caractere especial (!@#$%^&amp;*...)
              </p>

              {errors.submit && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.submit}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white border-t-transparent mr-2"></div>
                    Salvando...
                  </>
                ) : 'Redefinir senha'}
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            © {currentYear} {companyName}. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};
